/**
 * Runtime-gated performance tracing for the decision path.
 *
 * Enable with `?perf=1` (or `?perf=overlay` for an on-screen readout), disable
 * with `?perf=0`. The flag is persisted in localStorage so it survives the
 * PWA's own reloads. When off, every exported hook is a single boolean check
 * and nothing is observed or allocated.
 *
 * Readout: `window.__perf.summary()` (p50/p95 per metric), `__perf.dump()`
 * (JSON), `__perf.autoPlay({ turns, mode, gapMs })` to drive decisions,
 * `__perf.seedGame()` to jump straight into a game, `__perf.calibrate()` for
 * a fixed CPU workload to compare machines.
 */
import type { GameLogicState } from "@types"
import GAMEDATA from "../features/default"
import { getRandomValidTriggerOrSlide } from "../app/gameLogic"

const STORAGE_KEY = "perf"

// The flag lives in sessionStorage (survives the app's own version-check
// localStorage.clear() on a fresh install) and is mirrored to localStorage so
// a new tab on the same device keeps it.
const readFlag = (): string => {
  if (typeof window === "undefined") return ""
  try {
    const query = new URLSearchParams(window.location.search).get(STORAGE_KEY)
    if (query !== null) {
      if (query === "0" || query === "off") {
        window.sessionStorage.removeItem(STORAGE_KEY)
        window.localStorage.removeItem(STORAGE_KEY)
      } else {
        const value = query === "" ? "1" : query
        window.sessionStorage.setItem(STORAGE_KEY, value)
        window.localStorage.setItem(STORAGE_KEY, value)
      }
    }
    const value =
      window.sessionStorage.getItem(STORAGE_KEY) ??
      window.localStorage.getItem(STORAGE_KEY) ??
      ""
    if (value) window.sessionStorage.setItem(STORAGE_KEY, value)
    return value
  } catch {
    return ""
  }
}

const MODE = readFlag()
export const PERF_ENABLED = MODE !== ""
const OVERLAY = MODE === "overlay"

export type PerfEventKind =
  | "ack-paint"
  | "commit"
  | "text-paint"
  | "image-load"
  | "image-paint"
  | "flash"
  | "unlock"

export interface PerfDecisionMeta {
  slide?: string
  choice?: string
  image?: string
  historyLen?: number
}

export interface PerfDecision extends PerfDecisionMeta {
  id: number
  imageExt: string
  /** performance.now() when the choice handler ran */
  tapAt: number
  /** hardware input timestamp of the click (event.timeStamp) */
  eventAt?: number
  /** Event Timing: processingStart - startTime */
  inputDelay?: number
  /** Event Timing: startTime -> next paint after handlers */
  eventDuration?: number
  ackPaintAt?: number
  commitAt?: number
  textPaintAt?: number
  imageLoadAt?: number
  imagePaintAt?: number
  flashAt?: number
  unlockAt?: number
  swallowed: number
  longTasks: number[]
  renders: Record<string, number>
}

const decisions: PerfDecision[] = []
let current: PerfDecision | null = null
let rendersSinceTap: Record<string, number> = {}
let stateGetter: (() => GameLogicState) | null = null

const now = () => performance.now()

const finalizeRenders = () => {
  if (current) current.renders = { ...rendersSinceTap }
}

/** Called by the choice handler when a tap is accepted (lock taken). */
export const perfDecisionStart = (eventAt: number, meta: PerfDecisionMeta) => {
  if (!PERF_ENABLED) return
  finalizeRenders()
  rendersSinceTap = {}
  const ext = (meta.image ?? "").split(".").pop() ?? ""
  current = {
    id: decisions.length + 1,
    ...meta,
    imageExt: ext,
    tapAt: now(),
    eventAt,
    swallowed: 0,
    longTasks: [],
    renders: {},
  }
  decisions.push(current)
  performance.mark("decision:tap", { detail: meta })
}

/** Called when a tap was ignored because a choice is already in flight. */
export const perfSwallowedTap = () => {
  if (!PERF_ENABLED || !current) return
  current.swallowed += 1
}

const EVENT_FIELD: Record<PerfEventKind, keyof PerfDecision> = {
  "ack-paint": "ackPaintAt",
  commit: "commitAt",
  "text-paint": "textPaintAt",
  "image-load": "imageLoadAt",
  "image-paint": "imagePaintAt",
  flash: "flashAt",
  unlock: "unlockAt",
}

/** Records the first occurrence of a lifecycle event for the open decision. */
export const perfDecisionEvent = (kind: PerfEventKind) => {
  if (!PERF_ENABLED || !current) return
  const field = EVENT_FIELD[kind]
  if (current[field] !== undefined) return
  ;(current as unknown as Record<string, number>)[field] = now()
  performance.mark(`decision:${kind}`)
  if (kind === "unlock") {
    finalizeRenders()
    if (OVERLAY) renderOverlay()
  }
}

/** Counts renders of a component between taps. Not a React hook. */
export const usePerfRenderCount = (name: string) => {
  if (!PERF_ENABLED) return
  rendersSinceTap[name] = (rendersSinceTap[name] ?? 0) + 1
}

/** Lets the harness read the live game state without importing the store. */
export const perfRegisterStateGetter = (getter: () => GameLogicState) => {
  if (!PERF_ENABLED) return
  stateGetter = getter
}

// ---------------------------------------------------------------------------
// Observers
// ---------------------------------------------------------------------------

const decisionAt = (time: number): PerfDecision | undefined => {
  for (let i = decisions.length - 1; i >= 0; i -= 1) {
    const d = decisions[i]
    if (time >= d.tapAt - 100 && time <= d.tapAt + 3000) return d
    if (time > d.tapAt + 3000) return undefined
  }
  return undefined
}

const observe = (
  type: string,
  onEntries: (entries: PerformanceEntry[]) => void,
  extra: Record<string, unknown> = {},
) => {
  if (typeof PerformanceObserver === "undefined") return
  try {
    if (!PerformanceObserver.supportedEntryTypes?.includes(type)) return
    const observer = new PerformanceObserver(list =>
      onEntries(list.getEntries()),
    )
    observer.observe({ type, buffered: true, ...extra })
  } catch {
    // unsupported entry type on this browser
  }
}

interface EventTimingEntry extends PerformanceEntry {
  processingStart: number
  interactionId?: number
}

interface ElementTimingEntry extends PerformanceEntry {
  identifier: string
  renderTime: number
  loadTime: number
}

const installObservers = () => {
  observe("longtask", entries => {
    for (const entry of entries) {
      decisionAt(entry.startTime)?.longTasks.push(Math.round(entry.duration))
    }
  })
  observe(
    "event",
    entries => {
      for (const raw of entries) {
        const entry = raw as EventTimingEntry
        if (entry.name !== "click" && entry.name !== "pointerup") continue
        const d = decisions.find(
          x =>
            x.eventAt !== undefined &&
            Math.abs(x.eventAt - entry.startTime) < 80 &&
            x.eventDuration === undefined,
        )
        if (!d) continue
        d.inputDelay = Math.round(entry.processingStart - entry.startTime)
        d.eventDuration = Math.round(entry.duration)
      }
    },
    { durationThreshold: 16 },
  )
  observe("element", entries => {
    for (const raw of entries) {
      const entry = raw as ElementTimingEntry
      if (entry.identifier !== "slide-image") continue
      const paintAt = entry.renderTime || entry.loadTime
      const d = decisionAt(paintAt)
      if (d && d.imagePaintAt === undefined) d.imagePaintAt = paintAt
    }
  })
}

// ---------------------------------------------------------------------------
// Summary / dump
// ---------------------------------------------------------------------------

const percentile = (values: number[], p: number) => {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const index = Math.min(
    sorted.length - 1,
    Math.ceil((p / 100) * sorted.length) - 1,
  )
  return Math.round(sorted[Math.max(0, index)])
}

const rel = (d: PerfDecision, field: keyof PerfDecision) => {
  const value = d[field]
  return typeof value === "number" ? value - d.tapAt : undefined
}

const metric = (name: string, values: (number | undefined)[]) => {
  const present = values.filter((v): v is number => typeof v === "number")
  return {
    metric: name,
    n: present.length,
    p50: percentile(present, 50),
    p95: percentile(present, 95),
    max: present.length ? Math.round(Math.max(...present)) : null,
  }
}

const summarize = (subset: PerfDecision[]) => [
  metric(
    "inputDelay (event timing)",
    subset.map(d => d.inputDelay),
  ),
  metric(
    "tap→next paint (event timing)",
    subset.map(d => d.eventDuration),
  ),
  metric(
    "tap→ack paint",
    subset.map(d => rel(d, "ackPaintAt")),
  ),
  metric(
    "tap→commit",
    subset.map(d => rel(d, "commitAt")),
  ),
  metric(
    "tap→text paint",
    subset.map(d => rel(d, "textPaintAt")),
  ),
  metric(
    "tap→image load",
    subset.map(d => rel(d, "imageLoadAt")),
  ),
  metric(
    "tap→image paint",
    subset.map(d => rel(d, "imagePaintAt")),
  ),
  metric(
    "image paint − text paint",
    subset.map(d =>
      d.imagePaintAt !== undefined && d.textPaintAt !== undefined
        ? d.imagePaintAt - d.textPaintAt
        : undefined,
    ),
  ),
  metric(
    "tap→flash",
    subset.map(d => rel(d, "flashAt")),
  ),
  metric(
    "tap→unlock",
    subset.map(d => rel(d, "unlockAt")),
  ),
  metric(
    "long-task ms in 3 s",
    subset.map(d => d.longTasks.reduce((a, b) => a + b, 0)),
  ),
  metric(
    "longest task",
    subset.map(d => Math.max(0, ...d.longTasks)),
  ),
  metric(
    "renders (all components)",
    subset.map(d => Object.values(d.renders).reduce((a, b) => a + b, 0)),
  ),
  metric(
    "swallowed taps",
    subset.map(d => d.swallowed),
  ),
]

const summary = () => {
  finalizeRenders()
  const all = summarize(decisions)
  const byExt: Record<string, ReturnType<typeof summarize>> = {}
  for (const ext of new Set(decisions.map(d => d.imageExt))) {
    byExt[ext] = summarize(decisions.filter(d => d.imageExt === ext))
  }
  const renders: Record<string, number> = {}
  for (const d of decisions) {
    for (const [name, count] of Object.entries(d.renders)) {
      renders[name] = (renders[name] ?? 0) + count
    }
  }
  const perDecisionRenders = Object.fromEntries(
    Object.entries(renders).map(([k, v]) => [
      k,
      Math.round((v / Math.max(1, decisions.length)) * 10) / 10,
    ]),
  )
  // eslint-disable-next-line no-console
  console.table(all)
  return { decisions: decisions.length, all, byExt, perDecisionRenders }
}

const dump = () => {
  finalizeRenders()
  return JSON.stringify(
    {
      userAgent: navigator.userAgent,
      viewport: {
        w: window.innerWidth,
        h: window.innerHeight,
        dpr: window.devicePixelRatio,
      },
      decisions,
      summary: summary(),
    },
    null,
    1,
  )
}

// ---------------------------------------------------------------------------
// Driver helpers (used by autoPlay and the CDP script)
// ---------------------------------------------------------------------------

const MENTOR_CODE = "greenlight"

const choiceButtons = () =>
  Array.from(
    document.querySelectorAll<HTMLButtonElement>('button[aria-label="choice"]'),
  ).filter(b => !b.disabled)

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

const waitFor = async (predicate: () => boolean, timeoutMs: number) => {
  const deadline = now() + timeoutMs
  while (now() < deadline) {
    if (predicate()) return true
    await sleep(25)
  }
  return predicate()
}

const isIdle = () => current === null || current.unlockAt !== undefined

const tap = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect()
  const init = {
    bubbles: true,
    cancelable: true,
    clientX: rect.left + rect.width / 2,
    clientY: rect.top + rect.height / 2,
    pointerType: "touch",
    isPrimary: true,
  }
  el.dispatchEvent(new PointerEvent("pointerdown", init))
  el.dispatchEvent(new PointerEvent("pointerup", init))
  el.click()
}

const handleMentorModal = () => {
  const input = document.querySelector<HTMLInputElement>(
    'input[placeholder="Enter mentor code"]',
  )
  if (!input) return false
  const setter = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value",
  )?.set
  setter?.call(input, MENTOR_CODE)
  input.dispatchEvent(new Event("input", { bubbles: true }))
  const button = Array.from(document.querySelectorAll("button")).find(b =>
    b.textContent?.includes("Continue Game"),
  )
  button?.click()
  return true
}

const historyLen = () => stateGetter?.().history.length ?? -1

interface AutoPlayOptions {
  turns?: number
  /** single: one tap per turn; double: re-tap the same button after gapMs;
   *  late-double: re-tap the same screen position after gapMs */
  mode?: "single" | "double" | "late-double"
  gapMs?: number
  waitMs?: number
}

const autoPlay = async ({
  turns = 30,
  mode = "single",
  gapMs = mode === "late-double" ? 550 : 80,
  waitMs = 5000,
}: AutoPlayOptions = {}) => {
  const result = { turns: 0, wrongAnswers: 0, timeouts: 0, mentorGates: 0 }
  for (let i = 0; i < turns; i += 1) {
    if (handleMentorModal()) {
      result.mentorGates += 1
      await waitFor(isIdle, waitMs)
    }
    const buttons = choiceButtons()
    if (buttons.length === 0) break
    const target = buttons[Math.floor(Math.random() * buttons.length)]
    const before = historyLen()
    const rect = target.getBoundingClientRect()
    tap(target)
    if (mode !== "single") {
      await sleep(gapMs)
      const again =
        mode === "double"
          ? target
          : (document.elementFromPoint(
              rect.left + rect.width / 2,
              rect.top + rect.height / 2,
            ) as HTMLElement | null)
      const button = again?.closest<HTMLButtonElement>(
        'button[aria-label="choice"]',
      )
      if (button) tap(button)
    }
    const settled = await waitFor(isIdle, waitMs)
    if (!settled) result.timeouts += 1
    await waitFor(() => !handleMentorModalPending(), 50)
    if (handleMentorModal()) {
      result.mentorGates += 1
      await waitFor(isIdle, waitMs)
    }
    const advanced = historyLen() - before
    if (before >= 0 && advanced > 1) result.wrongAnswers += 1
    result.turns += 1
    await sleep(150)
  }
  return result
}

const handleMentorModalPending = () =>
  document.querySelector('input[placeholder="Enter mentor code"]') !== null

/** Fixed CPU workload, so laptop/tablet ratios can be compared. */
const calibrate = () => {
  const t0 = now()
  let sink = 0
  for (let i = 0; i < 5; i += 1) {
    sink += JSON.parse(JSON.stringify(GAMEDATA.content)).length
  }
  const numbers = Array.from({ length: 200_000 }, (_, i) => (i * 7919) % 10_007)
  numbers.sort((a, b) => a - b)
  sink += numbers[0]
  return Math.round(now() - t0) + sink * 0
}

/**
 * Writes a mid-game save + auth into localStorage so a reload lands in play.
 * On a freshly loaded page redux-persist is still writing its initial state
 * one key per second and flushes it on pagehide, which would overwrite the
 * seed; so the seed is written again after that flush.
 */
const seedGame = ({ turn = 1 }: { turn?: number } = {}) => {
  const start = GAMEDATA.start
  const state: GameLogicState = {
    ...start,
    turn,
    age: start.age + turn,
    tags: [],
    history: [
      {
        slide: start.slide?.title.en,
        action: "Continue",
        effects: undefined,
        totalImpact: 0,
      },
    ],
  }
  state.slide =
    getRandomValidTriggerOrSlide(GAMEDATA.content, state) ?? start.slide
  const write = () => {
    localStorage.setItem("authExpiry", String(Date.now() + 3_600_000))
    localStorage.setItem(
      "persist:root",
      JSON.stringify({
        game: JSON.stringify(state),
        _persist: JSON.stringify({ version: 1, rehydrated: true }),
      }),
    )
  }
  write()
  // The store's own pagehide listener was registered first, so this runs
  // after its flush and the seed is what the next load reads.
  window.addEventListener("pagehide", write, { once: true })
  return state.slide?.title.en
}

const cacheStatus = async () => {
  if (!("caches" in window)) return { entries: 0, precache: false }
  const names = await caches.keys()
  const name = names.find(n => n.includes("precache"))
  if (!name) return { entries: 0, precache: false }
  const keys = await (await caches.open(name)).keys()
  return { entries: keys.length, precache: true, name }
}

// ---------------------------------------------------------------------------
// Overlay
// ---------------------------------------------------------------------------

let overlay: HTMLDivElement | null = null

const renderOverlay = () => {
  if (!overlay) {
    overlay = document.createElement("div")
    overlay.id = "perf-overlay"
    overlay.style.cssText =
      "position:fixed;left:4px;bottom:4px;z-index:2147483647;font:11px/1.3 monospace;" +
      "background:rgba(0,0,0,.75);color:#9f9;padding:3px 6px;border-radius:4px;pointer-events:none;white-space:pre"
    document.body.appendChild(overlay)
  }
  const d = decisions[decisions.length - 1]
  if (!d) return
  const f = (v: number | undefined) => (v === undefined ? "–" : Math.round(v))
  const busy = d.longTasks.reduce((a, b) => a + b, 0)
  const renders = Object.values(d.renders).reduce((a, b) => a + b, 0)
  overlay.textContent =
    `#${d.id} ack ${f(rel(d, "ackPaintAt"))} text ${f(rel(d, "textPaintAt"))} ` +
    `img ${f(rel(d, "imagePaintAt") ?? rel(d, "imageLoadAt"))} busy ${busy} ` +
    `renders ${renders} swallowed ${d.swallowed}${cacheLine}`
}

let cacheLine = ""
const pollCache = async () => {
  if (!OVERLAY) return
  for (let i = 0; i < 120; i += 1) {
    const status = await cacheStatus()
    cacheLine = status.precache ? ` cache ${status.entries}` : " cache –"
    if (overlay) renderOverlay()
    await sleep(5000)
  }
}

// ---------------------------------------------------------------------------
// Install
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    __perf?: {
      decisions: PerfDecision[]
      summary: typeof summary
      dump: typeof dump
      autoPlay: typeof autoPlay
      calibrate: typeof calibrate
      seedGame: typeof seedGame
      cacheStatus: typeof cacheStatus
      handleMentorModal: typeof handleMentorModal
      isIdle: typeof isIdle
      choiceButtons: typeof choiceButtons
      reset: () => void
    }
  }
}

if (PERF_ENABLED && typeof window !== "undefined") {
  installObservers()
  window.__perf = {
    decisions,
    summary,
    dump,
    autoPlay,
    calibrate,
    seedGame,
    cacheStatus,
    handleMentorModal,
    isIdle,
    choiceButtons,
    reset: () => {
      decisions.length = 0
      current = null
      rendersSinceTap = {}
    },
  }
  void pollCache()
}
